import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../../api/AccountsAPI/verifyBankDepositAPI';

const err = (e, fallback) => e?.ErrorDetails?.ErrorMessage || e?.Message || e?.message || fallback;

export const fetchVerifyDepositQueue = createAsyncThunk(
    'verifyBankDeposit/fetchQueue',
    async (roleId, { rejectWithValue }) => {
        try { return await api.getVerificationDeposits(roleId); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch bank deposit verification queue')); }
    }
);

export const fetchVerifyDepositDetail = createAsyncThunk(
    'verifyBankDeposit/fetchDetail',
    async (bankTranId, { rejectWithValue }) => {
        try { return await api.getVerificationDepositById(bankTranId); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch deposit details')); }
    }
);

export const fetchReturnedDepositDetail = createAsyncThunk(
    'verifyBankDeposit/fetchReturnedDetail',
    async (bankTranId, { rejectWithValue }) => {
        try { return await api.getReturnedDepositById(bankTranId); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch returned deposit details')); }
    }
);

export const fetchDepositBanks = createAsyncThunk(
    'verifyBankDeposit/fetchBanks',
    async (_, { rejectWithValue }) => {
        try { return await api.getBanksForDeposit(); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch banks')); }
    }
);

export const submitVerifyDeposit = createAsyncThunk(
    'verifyBankDeposit/verify',
    async (payload, { rejectWithValue }) => {
        try { return await api.verifyDeposit(payload); }
        catch (e) { return rejectWithValue(err(e, 'Failed to submit deposit verification')); }
    }
);

export const submitUpdateDeposit = createAsyncThunk(
    'verifyBankDeposit/update',
    async (payload, { rejectWithValue }) => {
        try { return await api.updateDeposit(payload); }
        catch (e) { return rejectWithValue(err(e, 'Failed to update returned deposit')); }
    }
);

const initialState = {
    queue: [], queueLoading: false, queueError: null,
    detail: null, detailLoading: false, detailError: null,
    banks: [], banksLoading: false,
    verifyLoading: false, updateLoading: false,
};

// The pending-detail endpoint returns a one-row list; the returned-detail endpoint a single object.
const firstRow = (data) => (Array.isArray(data) ? data[0] || null : data || null);

const verifyBankDepositSlice = createSlice({
    name: 'verifyBankDeposit',
    initialState,
    reducers: {
        clearVerifyDepositDetail: (s) => { s.detail = null; s.detailError = null; s.detailLoading = false; },
        resetVerifyBankDeposit: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchVerifyDepositQueue.pending, (s) => { s.queueLoading = true; s.queueError = null; })
            .addCase(fetchVerifyDepositQueue.fulfilled, (s, a) => { s.queueLoading = false; s.queue = a.payload?.Data || []; })
            .addCase(fetchVerifyDepositQueue.rejected, (s, a) => { s.queueLoading = false; s.queueError = a.payload; s.queue = []; });

        [fetchVerifyDepositDetail, fetchReturnedDepositDetail].forEach((thunk) => {
            builder
                .addCase(thunk.pending, (s) => { s.detailLoading = true; s.detailError = null; s.detail = null; })
                .addCase(thunk.fulfilled, (s, a) => { s.detailLoading = false; s.detail = firstRow(a.payload?.Data); })
                .addCase(thunk.rejected, (s, a) => { s.detailLoading = false; s.detailError = a.payload; s.detail = null; });
        });

        builder
            .addCase(fetchDepositBanks.pending, (s) => { s.banksLoading = true; })
            .addCase(fetchDepositBanks.fulfilled, (s, a) => { s.banksLoading = false; s.banks = a.payload?.Data || []; })
            .addCase(fetchDepositBanks.rejected, (s) => { s.banksLoading = false; s.banks = []; });

        builder
            .addCase(submitVerifyDeposit.pending, (s) => { s.verifyLoading = true; })
            .addCase(submitVerifyDeposit.fulfilled, (s) => { s.verifyLoading = false; })
            .addCase(submitVerifyDeposit.rejected, (s) => { s.verifyLoading = false; });

        builder
            .addCase(submitUpdateDeposit.pending, (s) => { s.updateLoading = true; })
            .addCase(submitUpdateDeposit.fulfilled, (s) => { s.updateLoading = false; })
            .addCase(submitUpdateDeposit.rejected, (s) => { s.updateLoading = false; });
    },
});

export const { clearVerifyDepositDetail, resetVerifyBankDeposit } = verifyBankDepositSlice.actions;

export const selectVBDQueue = (s) => s.verifyBankDeposit.queue;
export const selectVBDQueueLoading = (s) => s.verifyBankDeposit.queueLoading;
export const selectVBDQueueError = (s) => s.verifyBankDeposit.queueError;
export const selectVBDDetail = (s) => s.verifyBankDeposit.detail;
export const selectVBDDetailLoading = (s) => s.verifyBankDeposit.detailLoading;
export const selectVBDDetailError = (s) => s.verifyBankDeposit.detailError;
export const selectVBDBanks = (s) => s.verifyBankDeposit.banks;
export const selectVBDVerifyLoading = (s) => s.verifyBankDeposit.verifyLoading;
export const selectVBDUpdateLoading = (s) => s.verifyBankDeposit.updateLoading;

export default verifyBankDepositSlice.reducer;
